import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Minus, Plus, Trash2, Truck, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import { PortalPage, Card, Btn, ErrorNote } from "@/components/PortalPage";
import { inputCls } from "@/components/AuthShell";
import { lineKey, useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { ProductImage, usd } from "@/pages/store/Store";

const FIELDS = [
  ["business_name", "Business name", true], ["contact_name", "Contact name", true], ["contact_email", "Invoice email", true], ["contact_phone", "Phone", true],
  ["address_line1", "Street address", true], ["address_line2", "Apt / suite (optional)", false], ["city", "City", true], ["state", "State", true], ["postal_code", "ZIP", true], ["country", "Country", true],
];

const ShipQuote = ({ quote, busy }) => {
  if (busy) return <p className="flex items-center gap-2 text-xs text-neutral-400"><Loader2 size={14} className="animate-spin" /> Estimating shipping…</p>;
  if (!quote) return null;
  return (
    <div className="rounded-lg border border-[#333] p-3 text-sm" data-testid="ship-quote">
      <p className="flex items-center gap-2 font-semibold"><Truck size={16} className="text-[var(--accent)]" />
        {quote.requires_manual_quote || quote.total_usd == null ? "Freight — ALR will quote shipping on your invoice" : `Est. ${quote.method === "freight-ltl" ? "LTL freight" : "FedEx Ground"}: ${usd(quote.total_usd)}`}
      </p>
      {quote.notes?.map((n) => <p key={n} className="mt-1 text-xs text-neutral-500">{n}</p>)}
    </div>
  );
};

export default function StoreCart() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const { lines, subtotal, setQty, clear } = useCart();
  const prior = user.store_shipping_info || {};
  const [ship, setShip] = useState(() => Object.fromEntries(FIELDS.map(([k]) => [k, prior[k] || (k === "contact_email" ? user.email : k === "contact_name" ? user.full_name : k === "country" ? "USA" : "") || ""])));
  const [quote, setQuote] = useState(null);
  const [quoting, setQuoting] = useState(false);
  const [ackInvoice, setAckInvoice] = useState(false);
  const [ackShip, setAckShip] = useState(false);
  const [busy, setBusy] = useState(false);
  const [overnight, setOvernight] = useState(false);
  const overnightPrice = Math.max(0, ...lines.map((l) => l.product.overnight_usd || 0));
  const itemsBody = () => lines.map((l) => ({ product_id: l.product.id, quantity: l.quantity, ...(l.machine ? { machine: l.machine } : {}) }));
  const [err, setErr] = useState(null);

  const zip = ship.postal_code.trim();
  useEffect(() => {
    if (!lines.length || zip.length < 5) { setQuote(null); return; }
    const t = setTimeout(() => {
      setQuoting(true);
      api("/store/shipping/quote", { method: "POST", body: { items: itemsBody(), destination_zip: zip, residential: true } })
        .then(setQuote).catch(() => setQuote(null)).finally(() => setQuoting(false));
    }, 600);
    return () => clearTimeout(t);
  }, [zip, lines]);

  const missing = FIELDS.filter(([k, , req]) => req && ship[k].trim().length < 2).map(([, label]) => label);

  const submit = async () => {
    if (missing.length) return setErr(`Please fill in: ${missing.join(", ")}`);
    setBusy(true); setErr(null);
    try {
      const body = { items: itemsBody(), overnight: overnight && overnightPrice > 0, acknowledged_invoice_terms: true, confirmed_shipping_info: true, shipping_info: { ...ship, address_line2: ship.address_line2 || null } };
      const r = await api("/store/submit-order", { method: "POST", body });
      clear();
      refreshUser();
      const total = r.qbo_invoice?.total_amt ?? r.subtotal_usd;
      toast.success(r.qbo_invoice?.sent ? `Order submitted — a QuickBooks invoice for ${usd(total)} was emailed to ${ship.contact_email}. Click "Pay invoice online" in that email; your order ships once paid.` : "Order submitted — ALR will email your QuickBooks invoice shortly.", { duration: 9000 });
      navigate("/store/orders");
    } catch (e) { setErr(e.message || "Could not submit your order."); } finally { setBusy(false); }
  };

  return (
    <PortalPage eyebrow="ALR STORE" title="Your cart" back="/store" backLabel="Store" testId="cart-page" width="max-w-5xl">
      {!lines.length ? (
        <Card className="text-center" data-testid="cart-empty"><p className="font-bold">Your cart is empty</p><Btn className="mt-4" onClick={() => navigate("/store")} data-testid="cart-browse">Browse the store</Btn></Card>
      ) : (
        <div className="grid gap-6 lg:grid-cols-[1fr_380px]">
          <div className="space-y-3">
            {lines.map((l) => { const k = lineKey(l); return (
              <div key={k} className="flex items-center gap-3 rounded-2xl border border-[#333] bg-[#1A1A1A] p-3" data-testid={`cart-line-${k}`}>
                <ProductImage p={l.product} className="h-16 w-16 shrink-0" />
                <div className="min-w-0 flex-1"><p className="truncate text-sm font-semibold">{l.product.name}</p><p className="text-xs text-neutral-400">{usd(l.product.price_usd)} each</p>{l.machine && <p className="text-xs font-semibold text-[var(--accent)]" data-testid={`cart-machine-${k}`}>For: {l.machine} machine</p>}</div>
                <div className="flex items-center gap-1">
                  <button onClick={() => setQty(k, l.quantity - 1)} aria-label="Decrease" data-testid={`cart-minus-${k}`} className="flex h-9 w-9 items-center justify-center rounded-lg border border-[#333]"><Minus size={14} /></button>
                  <span className="w-8 text-center font-bold" data-testid={`cart-qty-${k}`}>{l.quantity}</span>
                  <button onClick={() => setQty(k, l.quantity + 1)} aria-label="Increase" data-testid={`cart-plus-${k}`} className="flex h-9 w-9 items-center justify-center rounded-lg border border-[#333]"><Plus size={14} /></button>
                </div>
                <p className="w-20 text-right font-bold">{usd(l.quantity * l.product.price_usd)}</p>
                <button onClick={() => setQty(k, 0)} aria-label="Remove" data-testid={`cart-remove-${k}`} className="p-2 text-neutral-500 hover:text-[#FF3333]"><Trash2 size={16} /></button>
              </div>
            ); })}
            <Card className="space-y-3" data-testid="shipping-form">
              <p className="font-display text-xl font-bold uppercase">Ship to & invoice</p>
              <div className="grid gap-3 sm:grid-cols-2">
                {FIELDS.map(([k, label]) => <input key={k} data-testid={`ship-${k}`} className={inputCls} placeholder={label} value={ship[k]} onChange={(e) => { setShip({ ...ship, [k]: e.target.value }); setErr(null); }} />)}
              </div>
            </Card>
          </div>
          <Card className="h-fit space-y-3 lg:sticky lg:top-6" data-testid="order-summary">
            <div className="flex justify-between text-sm"><span className="text-neutral-400">Subtotal</span><b data-testid="cart-subtotal">{usd(subtotal)}</b></div>
            {overnightPrice > 0 && (
              <label className={`flex cursor-pointer items-start gap-3 rounded-lg border p-3 text-sm ${overnight ? "border-[var(--accent)] bg-[var(--accent)]/10" : "border-[#333]"}`} data-testid="overnight-option">
                <input type="checkbox" className="mt-0.5 h-4 w-4 accent-[var(--accent)]" checked={overnight} onChange={(e) => setOvernight(e.target.checked)} data-testid="overnight-checkbox" />
                <span><b>Ship overnight: {usd(overnightPrice)}</b><span className="block text-xs text-neutral-400">Replaces the ground shipping estimate and is added to your invoice.</span></span>
              </label>
            )}
            {overnight && overnightPrice > 0 ? <div className="flex items-center gap-2 rounded-lg border border-[#333] p-3 text-sm font-semibold" data-testid="ship-quote-overnight"><Truck size={16} className="text-[var(--accent)]" />Overnight shipping: {usd(overnightPrice)}</div> : <ShipQuote quote={quote} busy={quoting} />}
            <p className="text-xs text-neutral-500">Shipping and any sales tax are added on your QuickBooks invoice.</p>
            {[[ackInvoice, setAckInvoice, "ack-invoice", "I understand shipping is NOT included and ALR will email me a QuickBooks invoice to pay online. My order ships after payment."], [ackShip, setAckShip, "ack-shipping", "I confirm the shipping and billing details above are correct."]].map(([v, set, id, text]) => (
              <label key={id} className="flex cursor-pointer items-start gap-3 rounded-lg border border-[#333] p-3 text-xs" data-testid={id}>
                <input type="checkbox" className="mt-0.5 h-4 w-4 accent-[var(--accent)]" checked={v} onChange={(e) => set(e.target.checked)} />{text}
              </label>
            ))}
            <ErrorNote>{err}</ErrorNote>
            <Btn busy={busy} disabled={!ackInvoice || !ackShip} onClick={submit} data-testid="submit-order"><Send size={16} /> Submit order</Btn>
          </Card>
        </div>
      )}
    </PortalPage>
  );
}
